// src/components/FeatureGate.tsx
//
// Route-level gate for flag-controlled surfaces (M2 A21). When the
// flag is disabled (the default), the child page is NOT rendered at
// all — no demo/localStorage rows can leak into the DOM — and a
// clear "not available in this build" notice is shown instead.

import type { ReactNode } from "react";
import { ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  FEATURE_FLAG_ENV,
  FEATURE_FLAG_LABELS,
  FEATURE_FLAG_REASONS,
  isFeatureEnabled,
  type FeatureFlag,
} from "@/lib/featureFlags";

export function FeatureDisabledNotice({ flag }: { flag: FeatureFlag }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="feature-disabled"
      data-flag={flag}
      className="mx-auto max-w-lg rounded-md border border-dashed border-border bg-bg-panel/50 p-8 text-center"
    >
      <ShieldOff
        className="mx-auto h-8 w-8 text-fg-quaternary"
        strokeWidth={1.5}
      />
      <h1 className="mt-4 text-xl font-510 tracking-tight text-fg-primary">
        {t("featureGate.title")}
      </h1>
      <p className="mt-2 text-sm text-fg-tertiary">
        {t("featureGate.body", {
          label: t(`featureGate.labels.${flag}`, FEATURE_FLAG_LABELS[flag]),
          reason: t(`featureGate.reasons.${flag}`, FEATURE_FLAG_REASONS[flag]),
        })}
      </p>
      <p className="mt-4 text-xs text-fg-quaternary">
        {t("featureGate.enable", { env: FEATURE_FLAG_ENV[flag] })}{" "}
        <code className="rounded bg-bg-elevated px-1 py-0.5 font-mono text-[11px]">
          {FEATURE_FLAG_ENV[flag]}=1
        </code>
      </p>
    </div>
  );
}

export function FeatureGate({
  flag,
  children,
}: {
  flag: FeatureFlag;
  children: ReactNode;
}) {
  if (!isFeatureEnabled(flag)) {
    return <FeatureDisabledNotice flag={flag} />;
  }
  return <>{children}</>;
}
